import { COURSES, ERA_LOOKS } from '@continuum/show-core';
import { Screen } from '../ui/Shell.js';
import { mono, sans } from '../ui/atoms.js';
import type { ScreenCopy } from '../ui/screenText.js';

const BG: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'linear-gradient(180deg,#060b18,#0a1122)',
  2: 'linear-gradient(180deg,#0d0618,#150a24)',
  3: 'linear-gradient(180deg,#120305,#1c0508)',
  4: 'linear-gradient(180deg,#140d05,#211609)',
  5: 'linear-gradient(180deg,#eae4d0,#e4dec9)',
};

/**
 * The plate, on the phone.
 *
 * It says what has landed and then stops. The food is the thing in the room and the
 * phone is not — a station with buttons here would pull twelve faces back down.
 */
export function CourseCard({ chapter, s }: { chapter: 1 | 3 | 5; s: ScreenCopy }) {
  const course = COURSES[chapter];
  const look = ERA_LOOKS[chapter];

  return (
    <Screen
      style={{ background: BG[chapter], color: look.fg }}
      footer={
        <div style={{ textAlign: 'center', fontFamily: mono, fontSize: 12, letterSpacing: 1.5, lineHeight: 1.45, color: look.acc }}>
          {s.eat}
        </div>
      }
    >
      <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, lineHeight: 1.45, color: look.acc }}>
        {s.dishOf(course.index, course.pairing)}
      </div>

      <div
        style={{
          marginTop: 26,
          fontFamily: sans,
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: 1.5,
          lineHeight: 1.05,
          textWrap: 'balance',
        }}
      >
        {course.name}
      </div>

      <div style={{ marginTop: 12, fontSize: 15, lineHeight: 1.5, opacity: 0.88 }}>
        {course.dish}
      </div>

      <div
        style={{
          marginTop: 22,
          padding: '14px 15px',
          borderRadius: 11,
          border: `1px solid ${look.acc}33`,
          background: `${look.acc}0d`,
          fontSize: 15,
          lineHeight: 1.5,
          opacity: 0.95,
        }}
      >
        {course.note}
      </div>

    </Screen>
  );
}

/**
 * The last picture on Earth.
 *
 * Chapter 1 only, and only once. It says NOW on the phones as well as on the TV because
 * twelve people looking at each other cannot all see the TV, and the whole point of
 * the beat is that they are looking at each other. The moment is the captain's click:
 * nothing counts down (Marek, 2026-10-24).
 */
export function PhotoCall({ s }: { startedAt: number; s: ScreenCopy }) {
  const look = ERA_LOOKS[1];

  return (
    <Screen style={{ background: BG[1], color: look.fg }}>
      <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 2, lineHeight: 1.4, color: look.acc }}>{s.photoKicker}</div>

      <div style={{ flex: 1, minHeight: 12 }} />

      <div
        style={{
          fontFamily: sans,
          fontSize: 32,
          fontWeight: 700,
          letterSpacing: 1.5,
          lineHeight: 1.04,
          textAlign: 'center',
          textWrap: 'balance',
        }}
      >
        {s.photoTitle}
      </div>

      <div
        style={{
          marginTop: 16,
          textAlign: 'center',
          fontSize: 17,
          lineHeight: 1.45,
          opacity: 0.95,
        }}
      >
        {s.photoInstruction}
      </div>

      <div
        style={{
          margin: '26px auto 0',
          width: 128,
          height: 128,
          borderRadius: '50%',
          border: `1px solid ${look.acc}55`,
          display: 'grid',
          placeItems: 'center',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: -6,
            borderRadius: '50%',
            border: `1px dashed ${look.acc}33`,
            animation: 'ccSwirl 22s linear infinite',
          }}
        />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 34, fontWeight: 700, letterSpacing: 3, lineHeight: 1 }}>{s.photoNow}</div>
        </div>
      </div>

      <div
        style={{
          marginTop: 20,
          textAlign: 'center',
          fontFamily: mono,
          fontSize: 13,
          letterSpacing: 0.5,
          color: look.acc,
          lineHeight: 1.55,
        }}
      >
        {s.photoLine}
      </div>

      <div style={{ flex: 1, minHeight: 20 }} />

      <div
        style={{
          textAlign: 'center',
          fontFamily: mono,
          fontSize: 12,
          letterSpacing: 1,
          color: '#8a9aa6',
        }}
      >
        {s.photoFooter}
      </div>
    </Screen>
  );
}
